import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import StatusBadge, { WARTELISTE_STATUS, PLATZANGEBOT_ZUSTAND, PlatzangebotBadge } from '../components/StatusBadge';
import PlatzangebotInfo from '../components/PlatzangebotInfo';
import { AntragWarteliste } from '../components/Aufnahmeantrag';
import Modal from '../components/Modal';
import EmailModal from '../components/EmailModal';
import WartelisteHinzufuegen from '../components/WartelisteHinzufuegen';
import { useAuth } from '../context/AuthContext';
import { hinweis } from '../components/Hinweise';

// ─── Konstanten ────────────────────────────────────────────────────────────

const ABZEICHEN_ORDER = ['pirat', 'seepferdchen', 'trixi', 'bronze', 'silber', 'gold', 'schwimmen_ab_10'];

const ABZEICHEN_LABELS = {
  keins: 'Keins', seepferdchen: 'Seepferdchen', trixi: 'Trixi',
  bronze: 'Bronze', silber: 'Silber', gold: 'Gold',
  pirat: 'Pirat', schwimmen_ab_10: 'Schwimmen ab 10',
};

const ABZEICHEN_FARBEN = {
  pirat:          { bg: '#fce4ec', color: '#880e4f' },
  seepferdchen:   { bg: '#e3f2fd', color: '#0d47a1' },
  trixi:          { bg: '#e8f5e9', color: '#1b5e20' },
  bronze:         { bg: '#fff3e0', color: '#e65100' },
  silber:         { bg: '#f5f5f5', color: '#424242' },
  gold:           { bg: '#fffde7', color: '#f57f17' },
  schwimmen_ab_10:{ bg: '#e8eaf6', color: '#1a237e' },
};

const STATUS_OPTIONS = ['neu', 'kontaktiert', 'platz_angeboten', 'angenommen', 'abgelehnt', 'storniert'];

// Status, zu denen es eine E-Mail-Vorlage an die Kontaktperson gibt
const STATUS_MAIL = {
  platz_angeboten: 'Platzangebot',
  abgelehnt:       'Abgelehnt',
};

// Mindestalter für Anmeldung (Jahre)
const MIN_ALTER_JAHRE = 5;

// ─── Hilfsfunktionen ───────────────────────────────────────────────────────

function calcAlterJahre(geb) {
  if (!geb) return null;
  return Math.floor((Date.now() - new Date(geb).getTime()) / (365.25 * 24 * 3600 * 1000));
}

function calcAlterLabel(geb) {
  const j = calcAlterJahre(geb);
  if (j === null) return '–';
  return `${j} J.`;
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('de-DE') : '–'; }
function fmtDateInput(d) { return d ? String(d).slice(0, 10) : ''; }

function calcWartezeit(created_at, wartetage) {
  if (!created_at) return '–';
  const tage = wartetage != null ? Math.floor(Number(wartetage)) : Math.floor((Date.now() - new Date(created_at).getTime()) / 86400000);
  if (tage < 1)   return 'Heute';
  if (tage < 7)   return `${tage} Tag${tage === 1 ? '' : 'e'}`;
  if (tage < 30)  return `${Math.floor(tage / 7)} Wo.`;
  if (tage < 365) return `${Math.floor(tage / 30)} Mon.`;
  const j = Math.floor(tage / 365);
  const m = Math.floor((tage % 365) / 30);
  return m > 0 ? `${j} J. ${m} Mon.` : `${j} J.`;
}

function parseCsvText(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { eintraege: [], fehler: ['Keine Datenzeilen gefunden'] };

  const header = lines[0].split(';').map(h => h.trim().replace(/^"|"$/g, ''));
  const eintraege = [];
  const fehler = [];

  const COL_MAP = {
    'anmeldedatum':         'anmeldedatum',
    'kontakt_nachname':     'kontakt_nachname',
    'kontakt nachname':     'kontakt_nachname',
    'kontakt_vorname':      'kontakt_vorname',
    'kontakt vorname':      'kontakt_vorname',
    'email':                'kontakt_email',
    'kontakt_email':        'kontakt_email',
    'telefon':              'kontakt_telefon',
    'kontakt_telefon':      'kontakt_telefon',
    'schwimmer_vorname':    'schwimmer_vorname',
    'schwimmer vorname':    'schwimmer_vorname',
    'schwimmer_nachname':   'schwimmer_nachname',
    'schwimmer nachname':   'schwimmer_nachname',
    'geburtsdatum':         'schwimmer_geburtsdatum',
    'schwimmer_geburtsdatum': 'schwimmer_geburtsdatum',
    'geschlecht':           'schwimmer_geschlecht',
    'schwimmer_geschlecht': 'schwimmer_geschlecht',
    'letztes_abzeichen':    'letztes_abzeichen',
    'letztes abzeichen':    'letztes_abzeichen',
    'gewuenschtes_abzeichen': 'gewuenschtes_abzeichen',
    'gewünschtes abzeichen':  'gewuenschtes_abzeichen',
    'gesundheitliche_hinweise': 'gesundheitliche_hinweise',
    'bemerkung':            'gesundheitliche_hinweise',
  };

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const vals = lines[i].split(';').map(v => v.trim().replace(/^"|"$/g, ''));
    const obj = {};
    header.forEach((h, idx) => {
      const key = COL_MAP[h.toLowerCase()];
      if (key) obj[key] = vals[idx] || '';
    });
    // Datumsformat normalisieren: dd.mm.yyyy → yyyy-mm-dd
    ['anmeldedatum', 'schwimmer_geburtsdatum'].forEach(f => {
      if (obj[f] && /^\d{2}\.\d{2}\.\d{4}$/.test(obj[f])) {
        const [d, m, y] = obj[f].split('.');
        obj[f] = `${y}-${m}-${d}`;
      }
    });
    // Geschlecht normalisieren
    if (obj.schwimmer_geschlecht) {
      const g = obj.schwimmer_geschlecht.toLowerCase();
      if (['m', 'männlich', 'maennlich', 'junge', 'boy'].includes(g)) obj.schwimmer_geschlecht = 'maennlich';
      else if (['w', 'weiblich', 'mädchen', 'maedchen', 'girl'].includes(g)) obj.schwimmer_geschlecht = 'weiblich';
      else if (['d', 'divers'].includes(g)) obj.schwimmer_geschlecht = 'divers';
    }
    eintraege.push(obj);
  }

  return { eintraege, fehler };
}

// ─── Haupt-Komponente ──────────────────────────────────────────────────────

export default function Warteliste() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const darfHinzufuegen = ['admin', 'schwimmadmin'].includes(user?.rolle);
  const [hinzufuegenOffen, setHinzufuegenOffen] = useState(false);
  const [bearbeitenEintrag, setBearbeitenEintrag] = useState(null);
  const [hinzugefuegt, setHinzugefuegt] = useState('');
  const [activeTab, setActiveTab]   = useState('alle');
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAngebot, setFilterAngebot] = useState('');
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  // Kategorie-Zählungen für Tabs
  const [kategorieZahlen, setKategorieZahlen] = useState({});

  // Detail-Modal
  const [selected, setSelected]     = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editBemerkung, setEditBemerkung] = useState('');
  const [saving, setSaving]         = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [emailModal, setEmailModal]  = useState(null);
  // E-Mail beim Statuswechsel auf „Platz angeboten" / „Abgelehnt" vorschlagen
  const [statusMail, setStatusMail]  = useState(true);

  // Reihenfolge-Notiz
  const [reorderNotiz, setReorderNotiz]   = useState('');
  const [reorderModal, setReorderModal]   = useState(null); // { id, direction }
  const [reordering, setReordering]       = useState(false);

  // Duplikat-Panel
  const [duplikate, setDuplikate]         = useState([]);
  const [duplikatOffen, setDuplikatOffen] = useState(false);
  const [duplikatAktion, setDuplikatAktion] = useState(null); // { paar, art, behalten_id, loeschen_id }
  const [duplikatSaving, setDuplikatSaving] = useState(false);
  // Manuelle Auswahl: { [paarKey]: behalten_id } — überschreibt Systemempfehlung
  const [duplikatWahl, setDuplikatWahl]   = useState({});

  // Import-Modal
  const [importModal, setImportModal] = useState(false);
  const [importTab, setImportTab]     = useState('excel'); // 'excel' | 'csv' | 'manuell'
  const [csvText, setCsvText]         = useState('');
  const [csvPreview, setCsvPreview]   = useState(null);
  const [importing, setImporting]     = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Excel-Upload
  const [excelDatei, setExcelDatei]   = useState(null); // { name, file }
  const excelInputRef = useRef(null);

  // Manueller Eintrag
  const LEER_EINTRAG = {
    anmeldedatum: '', kontakt_nachname: '', kontakt_vorname: '', kontakt_email: '',
    kontakt_telefon: '', schwimmer_vorname: '', schwimmer_nachname: '',
    schwimmer_geburtsdatum: '', schwimmer_geschlecht: '', letztes_abzeichen: '',
    gewuenschtes_abzeichen: '', gesundheitliche_hinweise: '',
  };
  const [manuellEintraege, setManuellEintraege] = useState([{ ...LEER_EINTRAG }]);

  const fileInputRef = useRef(null);

  // ── Laden ──────────────────────────────────────────────────────────────

  const loadKategorien = useCallback(async () => {
    try {
      // Alle laden für Zählungen
      const res = await api.get('/api/admin/warteliste', { params: { limit: 1000, page: 1 } });
      const zahlen = {};
      (res.data.data || []).forEach(r => {
        zahlen[r.gewuenschtes_abzeichen] = (zahlen[r.gewuenschtes_abzeichen] || 0) + 1;
      });
      setKategorieZahlen(zahlen);
      setTotal(res.data.pagination?.total || 0);
    } catch { /* ignoriert */ }
  }, []);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    setError('');
    try {
      let params = {};
      if (activeTab === 'alle') {
        params = { page: p, limit: 20 };
        if (search) params.search = search;
        if (filterStatus) params.status = filterStatus;
        if (filterAngebot) params.platzangebot = filterAngebot;
      } else {
        params = { abzeichen: activeTab };
      }
      const res = await api.get('/api/admin/warteliste', { params });
      setRows(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
      setPages(res.data.pagination?.pages || 1);
    } catch {
      setError('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, filterStatus, filterAngebot]);

  const loadDuplikate = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/warteliste/duplikate');
      setDuplikate(r.data || []);
    } catch { /* ignoriert */ }
  }, []);

  useEffect(() => { loadKategorien(); loadDuplikate(); }, []); // eslint-disable-line
  useEffect(() => { setPage(1); load(1); }, [activeTab, search, filterStatus, filterAngebot]); // eslint-disable-line
  useEffect(() => { if (activeTab === 'alle') load(page); }, [page]); // eslint-disable-line

  // ── Detail-Modal ───────────────────────────────────────────────────────

  const openModal = (row) => {
    setSelected(row);
    setEditStatus(row.status);
    setEditBemerkung(row.bemerkung_intern || '');
    setDeleteConfirm(false);
    setStatusMail(true);
  };
  const closeModal = () => { setSelected(null); setDeleteConfirm(false); };

  // Wird beim Speichern eine Status-E-Mail angeboten?
  const bietetStatusMail = !!selected && editStatus !== selected.status
    && Object.prototype.hasOwnProperty.call(STATUS_MAIL, editStatus) && !!selected.kontakt_email;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/warteliste/${selected.id}`, {
        status: editStatus, bemerkung_intern: editBemerkung,
      });
      const eintrag = selected;
      const mailStatus = bietetStatusMail && statusMail ? editStatus : null;
      await load(page);
      await loadKategorien();
      closeModal();

      // Status ist gespeichert – jetzt die vorausgefüllte E-Mail zum Prüfen öffnen
      if (mailStatus) {
        try {
          const r = await api.get(`/api/admin/warteliste/${eintrag.id}/email-vorlage/${mailStatus}`);
          setEmailModal({ ...eintrag, vorlage: { status: mailStatus, betreff: r.data.betreff, nachricht: r.data.nachricht } });
        } catch (err) {
          hinweis.fehler(`Status gespeichert. Die E-Mail konnte nicht vorbereitet werden: ${err.response?.data?.error || 'Fehler beim Laden der Vorlage'}`);
        }
      }
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) { setDeleteConfirm(true); return; }
    setSaving(true);
    try {
      await api.delete(`/api/admin/warteliste/${selected.id}`);
      await load(page);
      await loadKategorien();
      closeModal();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Löschen');
    } finally { setSaving(false); }
  };

  // ── Reorder ────────────────────────────────────────────────────────────

  const initiateReorder = (id, direction) => {
    setReorderNotiz('');
    setReorderModal({ id, direction });
  };

  const confirmReorder = async () => {
    if (!reorderModal) return;
    setReordering(true);
    try {
      await api.post('/api/admin/warteliste/reorder', {
        id: reorderModal.id,
        direction: reorderModal.direction,
        notiz: reorderNotiz || null,
      });
      setReorderModal(null);
      await load(page);
      await loadKategorien();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Umsortieren');
    } finally { setReordering(false); }
  };

  // ── Duplikate ──────────────────────────────────────────────────────────

  const handleDuplikatAktion = async () => {
    if (!duplikatAktion) return;
    setDuplikatSaving(true);
    try {
      if (duplikatAktion.art === 'zusammenfuehren') {
        await api.post('/api/admin/warteliste/duplikate/zusammenfuehren', {
          behalten_id: duplikatAktion.behalten_id,
          loeschen_id: duplikatAktion.loeschen_id,
        });
        // Manuelle Wahl für dieses Paar zurücksetzen
        const key = `${duplikatAktion.paar.id_1}_${duplikatAktion.paar.id_2}`;
        setDuplikatWahl(w => { const n = { ...w }; delete n[key]; return n; });
      } else {
        await api.post('/api/admin/warteliste/duplikate/ignorieren', {
          id_1: duplikatAktion.paar.id_1,
          id_2: duplikatAktion.paar.id_2,
        });
      }
      await Promise.all([loadDuplikate(), load(page), loadKategorien()]);
      setDuplikatAktion(null);
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler');
    } finally { setDuplikatSaving(false); }
  };

  // ── Export ─────────────────────────────────────────────────────────────

  const handleExport = async () => {
    try {
      const res = await api.get('/api/admin/warteliste/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `warteliste_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { hinweis.fehler('Export fehlgeschlagen'); }
  };

  // ── Import ─────────────────────────────────────────────────────────────

  const handleCsvParse = () => {
    const { eintraege, fehler } = parseCsvText(csvText);
    setCsvPreview({ eintraege, fehler });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setCsvText(ev.target.result); setCsvPreview(null); };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const handleImportSubmit = async (eintraege) => {
    setImporting(true);
    setImportResult(null);
    try {
      const res = await api.post('/api/admin/warteliste/import', { eintraege });
      setImportResult(res.data);
      if (res.data.importiert > 0) {
        await load(1);
        await loadKategorien();
      }
    } catch (err) {
      setImportResult({ fehler: [{ zeile: 0, fehler: err.response?.data?.error || 'Unbekannter Fehler' }] });
    } finally { setImporting(false); }
  };

  const handleManuellImport = () => {
    handleImportSubmit(manuellEintraege.filter(e => e.kontakt_nachname && e.schwimmer_vorname));
  };

  const updateManuellEintrag = (idx, field, val) => {
    setManuellEintraege(prev => prev.map((e, i) => i === idx ? { ...e, [field]: val } : e));
  };

  const closeImport = () => {
    setImportModal(false);
    setImportTab('excel');
    setCsvText('');
    setCsvPreview(null);
    setImportResult(null);
    setExcelDatei(null);
    setManuellEintraege([{ ...LEER_EINTRAG }]);
  };

  const handleVorlageDownload = async () => {
    try {
      const res = await api.get('/api/admin/warteliste/import/vorlage', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'warteliste_vorlage.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch { hinweis.fehler('Download fehlgeschlagen'); }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setExcelDatei({ name: file.name, file });
    setImportResult(null);
    e.target.value = '';
  };

  const handleExcelImport = async () => {
    if (!excelDatei) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('datei', excelDatei.file);
      const res = await api.post('/api/admin/warteliste/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      if (res.data.importiert > 0) {
        await load(1);
        await loadKategorien();
      }
    } catch (err) {
      setImportResult({ fehler: [{ zeile: 0, fehler: err.response?.data?.error || 'Unbekannter Fehler' }] });
    } finally { setImporting(false); }
  };

  // ── Regeln-Panel ──────────────────────────────────────────────────────
  const [regelnOffen, setRegelnOffen] = useState(false);

  // ── Tabs ───────────────────────────────────────────────────────────────

  const tabsData = [
    { key: 'alle', label: 'Alle', count: total },
    ...ABZEICHEN_ORDER
      .filter(a => kategorieZahlen[a] > 0)
      .map(a => ({ key: a, label: ABZEICHEN_LABELS[a], count: kategorieZahlen[a] || 0 })),
  ];

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h2 className="page-title">
          Warteliste <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '1rem' }}>({total})</span>
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {darfHinzufuegen && (
            <button className="btn btn-primary btn-sm" onClick={() => { setHinzugefuegt(''); setHinzufuegenOffen(true); }}>
              + Hinzufügen
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setImportModal(true)}>📥 Import</button>
          <button className="btn btn-secondary btn-sm" onClick={handleExport}>📤 CSV Export</button>
        </div>
      </div>

      {hinzugefuegt && (
        <div className="alert alert-success" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ flex: 1 }}>{hinzugefuegt}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setHinzugefuegt('')}>OK</button>
        </div>
      )}

      {bearbeitenEintrag && (
        <WartelisteHinzufuegen
          eintrag={bearbeitenEintrag}
          onClose={() => { const e = bearbeitenEintrag; setBearbeitenEintrag(null); openModal(e); }}
          onGespeichert={async (neu) => {
            setBearbeitenEintrag(null);
            if (neu.geaendert?.length) {
              const positionText = neu.abzeichen_alt && neu.abzeichen_alt !== neu.gewuenschtes_abzeichen
                ? ` Neue Ausbildung „${ABZEICHEN_LABELS[neu.gewuenschtes_abzeichen]}", Position ${neu.reihenfolge}.`
                : neu.position_alt !== neu.reihenfolge ? ` Position ${neu.position_alt} → ${neu.reihenfolge}.` : '';
              setHinzugefuegt(`Daten von ${neu.schwimmer_vorname} ${neu.schwimmer_nachname} wurden gespeichert.${positionText}`);
            }
            await load(page);
            await loadKategorien();
            openModal(neu);
          }}
        />
      )}

      {hinzufuegenOffen && (
        <WartelisteHinzufuegen
          onClose={() => setHinzufuegenOffen(false)}
          onGespeichert={async (neu) => {
            setHinzufuegenOffen(false);
            const kategorie = ABZEICHEN_LABELS[neu.gewuenschtes_abzeichen] || neu.gewuenschtes_abzeichen;
            setHinzugefuegt(
              `${neu.schwimmer_vorname} ${neu.schwimmer_nachname} wurde auf die Warteliste „${kategorie}" gesetzt (Position ${neu.reihenfolge}).`
              + (neu.nachgerueckt > 0 ? ` ${neu.nachgerueckt} später angemeldete Einträge sind eine Position nach hinten gerückt.` : '')
            );
            await load(page);
            await loadKategorien();
          }}
        />
      )}

      {/* ── Duplikat-Warnung ──────────────────────────────────────────────── */}
      {duplikate.length > 0 && (
        <div style={{ border: '2px solid #f57c00', borderRadius: 8, marginBottom: '1rem', overflow: 'hidden' }}>
          <button
            onClick={() => setDuplikatOffen(o => !o)}
            style={{
              width: '100%', textAlign: 'left', background: '#fff8e1',
              border: 'none', padding: '0.75rem 1rem', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontWeight: 700, color: '#e65100',
            }}
          >
            <span>⚠ {duplikate.length} mögliche{duplikate.length === 1 ? 's Duplikat' : ' Duplikate'} erkannt – Entscheidung erforderlich</span>
            <span style={{ fontSize: '0.75rem', fontWeight: 400 }}>{duplikatOffen ? '▲ zuklappen' : '▼ prüfen'}</span>
          </button>

          {duplikatOffen && (
            <div style={{ background: '#fff', borderTop: '2px solid #f57c00', padding: '1rem' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Die folgenden Einträge haben identischen Namen und Geburtsdatum. Bitte prüfen Sie, ob es sich um dasselbe Kind handelt, und wählen Sie eine Aktion.
              </p>
              {duplikate.map((paar, idx) => {
                const ABZEICHEN_LABELS_LOCAL = {
                  pirat: 'Pirat 🏴‍☠️', seepferdchen: 'Seepferdchen 🐴', trixi: 'Trixi 🌟',
                  bronze: 'Bronze 🥉', silber: 'Silber 🥈', gold: 'Gold 🥇',
                  schwimmen_ab_10: 'Schwimmen ab 10', keins: 'Keins',
                };
                const key = `${paar.id_1}_${paar.id_2}`;
                // Aktive Wahl: manuelle Auswahl hat Vorrang, sonst Systemempfehlung
                const aktivBehaltenId = duplikatWahl[key] ?? paar.behalten_id;
                const aktivLoeschenId = aktivBehaltenId === paar.id_1 ? paar.id_2 : paar.id_1;
                const istManuell = duplikatWahl[key] != null && duplikatWahl[key] !== paar.behalten_id;

                const seiten = [{
                  id: paar.id_1, abzeichen: paar.abzeichen_1, datum: paar.anmeldedatum_1 || paar.erstellt_1,
                  kontakt: `${paar.kontakt_vorname_1} ${paar.kontakt_nachname_1}`, email: paar.kontakt_email_1,
                  status: paar.status_1, systemEmpfehlung: paar.behalten_id === paar.id_1,
                }, {
                  id: paar.id_2, abzeichen: paar.abzeichen_2, datum: paar.anmeldedatum_2 || paar.erstellt_2,
                  kontakt: `${paar.kontakt_vorname_2} ${paar.kontakt_nachname_2}`, email: paar.kontakt_email_2,
                  status: paar.status_2, systemEmpfehlung: paar.behalten_id === paar.id_2,
                }];

                return (
                  <div key={idx} style={{
                    border: '1px solid #ffcc02', borderRadius: 8, padding: '1rem',
                    marginBottom: idx < duplikate.length - 1 ? '1rem' : 0, background: '#fffde7',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#e65100' }}>
                        Mögliches Duplikat #{idx + 1}: {paar.vorname_1} {paar.nachname_1}
                      </span>
                      {istManuell && (
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700,
                          background: '#e8eaf6', color: '#3949ab' }}>✋ Manuell gewählt</span>
                      )}
                    </div>

                    {/* Nebeneinander-Vergleich mit Auswahl */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.75rem', marginBottom: '0.875rem' }}>
                      {seiten.map((s) => {
                        const istGewaehlt = s.id === aktivBehaltenId;
                        return (
                          <div key={s.id} style={{
                            padding: '0.75rem', borderRadius: 6, fontSize: '0.82rem', lineHeight: 1.7,
                            background: istGewaehlt ? '#e8f5e9' : '#fff3f3',
                            border: `2px solid ${istGewaehlt ? '#43a047' : '#ef9a9a'}`,
                            position: 'relative',
                          }}>
                            {/* Status-Label */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.375rem' }}>
                              <span style={{ fontWeight: 700, color: istGewaehlt ? '#2e7d32' : '#c62828', fontSize: '0.8rem' }}>
                                {istGewaehlt ? '✓ Wird behalten' : '✗ Wird gelöscht'}
                              </span>
                              {s.systemEmpfehlung && (
                                <span style={{ fontSize: '0.68rem', color: '#1565c0', background: '#e3f2fd',
                                  padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>System-Empfehlung</span>
                              )}
                            </div>

                            <div>ID: <strong>#{s.id}</strong></div>
                            <div>Abzeichen: <strong>{ABZEICHEN_LABELS_LOCAL[s.abzeichen] || s.abzeichen}</strong></div>
                            <div>Anmeldedatum: <strong>{fmtDate(s.datum)}</strong></div>
                            <div>Kontakt: {s.kontakt}</div>
                            <div style={{ color: 'var(--text-muted)' }}>{s.email}</div>

                            {/* Auswahl-Button — nur zeigen wenn dieser Eintrag NICHT aktiv gewählt ist */}
                            {!istGewaehlt && (
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ marginTop: '0.625rem', width: '100%', fontSize: '0.78rem' }}
                                onClick={() => setDuplikatWahl(w => ({ ...w, [key]: s.id }))}
                              >
                                ← Diesen behalten
                              </button>
                            )}
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>↔</div>
                    </div>

                    {/* Aktions-Buttons */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button className="btn btn-primary btn-sm"
                        onClick={() => setDuplikatAktion({ paar, art: 'zusammenfuehren', behalten_id: aktivBehaltenId, loeschen_id: aktivLoeschenId })}>
                        🔀 Zusammenführen
                        <span style={{ fontSize: '0.72rem', marginLeft: 4, opacity: 0.8 }}>
                          (behalte #{aktivBehaltenId}, lösche #{aktivLoeschenId})
                        </span>
                      </button>
                      {istManuell && (
                        <button className="btn btn-secondary btn-sm"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() => setDuplikatWahl(w => { const n = { ...w }; delete n[key]; return n; })}>
                          ↺ Empfehlung zurücksetzen
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm"
                        onClick={() => setDuplikatAktion({ paar, art: 'ignorieren', behalten_id: aktivBehaltenId, loeschen_id: aktivLoeschenId })}>
                        👥 Verschiedene Kinder
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Verwaltungsregeln */}
      <div style={{
        border: '1px solid var(--border)', borderRadius: 8,
        marginBottom: '1rem', overflow: 'hidden',
      }}>
        <button
          onClick={() => setRegelnOffen(o => !o)}
          style={{
            width: '100%', textAlign: 'left', background: '#f8f9fa',
            border: 'none', padding: '0.625rem 1rem', cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)',
          }}
        >
          <span>📋 Verwaltungsregeln der Warteliste</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {regelnOffen ? '▲ zuklappen' : '▼ anzeigen'}
          </span>
        </button>

        {regelnOffen && (
          <div style={{ padding: '1rem', background: '#fff', borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>

              <div style={{ background: '#e3f2fd', borderRadius: 8, padding: '0.875rem' }}>
                <div style={{ fontWeight: 700, color: '#1565c0', marginBottom: '0.4rem' }}>⏱ FIFO-Prinzip</div>
                <div style={{ fontSize: '0.82rem', color: '#1a237e', lineHeight: 1.6 }}>
                  Die Reihenfolge richtet sich nach dem <strong>Anmeldedatum</strong>.
                  Wer sich zuerst anmeldet, erhält zuerst einen freien Platz.
                  Das Anmeldedatum ist unveränderlich und wird bei jedem Eintrag gespeichert.
                </div>
              </div>

              <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '0.875rem' }}>
                <div style={{ fontWeight: 700, color: '#1b5e20', marginBottom: '0.4rem' }}>📂 Getrennte Kategorie-Listen</div>
                <div style={{ fontSize: '0.82rem', color: '#1b5e20', lineHeight: 1.6 }}>
                  Jedes Ausbildungsziel (Pirat, Seepferdchen, Trixi, Bronze, Silber, Gold,
                  Schwimmen ab 10) führt eine <strong>eigene Reihenfolge</strong>.
                  Ein Kind steht nur in der Liste des gewählten Abzeichens.
                </div>
              </div>

              <div style={{ background: '#fff3e0', borderRadius: 8, padding: '0.875rem' }}>
                <div style={{ fontWeight: 700, color: '#e65100', marginBottom: '0.4rem' }}>🎂 Mindestalter 5 Jahre</div>
                <div style={{ fontSize: '0.82rem', color: '#bf360c', lineHeight: 1.6 }}>
                  Ausbildungen werden erst <strong>ab 5 Jahren</strong> angeboten.
                  Jüngere Kinder sind mit <strong>⚠ &lt;5J</strong> markiert und die Zeile wird
                  gelb hervorgehoben. Sie bleiben auf der Liste und werden kontaktiert,
                  sobald sie das Mindestalter erreicht haben.
                </div>
              </div>

              <div style={{ background: '#fce4ec', borderRadius: 8, padding: '0.875rem' }}>
                <div style={{ fontWeight: 700, color: '#880e4f', marginBottom: '0.4rem' }}>✋ Manuelle Eingriffe</div>
                <div style={{ fontSize: '0.82rem', color: '#880e4f', lineHeight: 1.6 }}>
                  Änderungen an der Reihenfolge sind nur in <strong>begründeten Ausnahmefällen</strong>
                  erlaubt (z. B. Geschwisterkinder, nachträgliche Datumskorrekturen).
                  Jeder Eingriff wird dauerhaft mit <strong>Name, Datum und Begründung</strong> gespeichert
                  und ist für alle Admins sichtbar (<span style={{ background: '#fff3e0', color: '#e65100', padding: '0 4px', borderRadius: 3, fontSize: '0.78rem' }}>✋ Manuell</span>-Badge).
                </div>
              </div>

              <div style={{ background: '#f3e5f5', borderRadius: 8, padding: '0.875rem' }}>
                <div style={{ fontWeight: 700, color: '#6a1b9a', marginBottom: '0.4rem' }}>📥 Importierte Einträge</div>
                <div style={{ fontSize: '0.82rem', color: '#6a1b9a', lineHeight: 1.6 }}>
                  Einträge aus der Alt-Liste (manuell importiert) sind mit
                  <span style={{ background: '#e8eaf6', color: '#3949ab', padding: '0 4px', borderRadius: 3, fontSize: '0.78rem', margin: '0 3px' }}>📥 Import</span>
                  gekennzeichnet. Das originale <strong>Anmeldedatum</strong> wurde dabei übernommen,
                  sodass die FIFO-Reihenfolge auch für diese Einträge gewahrt bleibt.
                </div>
              </div>

              <div style={{ background: '#e8eaf6', borderRadius: 8, padding: '0.875rem' }}>
                <div style={{ fontWeight: 700, color: '#1a237e', marginBottom: '0.4rem' }}>🏷 Status-Bedeutungen</div>
                <div style={{ fontSize: '0.82rem', color: '#1a237e', lineHeight: 1.8 }}>
                  <div><strong>Neu</strong> – Anmeldung eingegangen, noch nicht bearbeitet</div>
                  <div><strong>Kontaktiert</strong> – Eltern wurden angesprochen</div>
                  <div><strong>Platz angeboten</strong> – Freier Platz wurde mitgeteilt</div>
                  <div><strong>Angenommen</strong> – Platz wurde bestätigt</div>
                  <div><strong>Abgelehnt / Storniert</strong> – Kein aktiver Wartelistenplatz mehr</div>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>

      {/* Kategorie-Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {tabsData.map(t => {
          const farbe = ABZEICHEN_FARBEN[t.key] || { bg: '#f0f4f8', color: 'var(--primary)' };
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '0.375rem 0.875rem',
                borderRadius: 20,
                border: isActive ? `2px solid ${farbe.color}` : '2px solid transparent',
                background: isActive ? farbe.bg : '#f8f9fa',
                color: isActive ? farbe.color : 'var(--text-muted)',
                fontWeight: isActive ? 700 : 400,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'all .15s',
              }}
            >
              {t.label}
              <span style={{
                marginLeft: '0.375rem', background: isActive ? farbe.color : '#dee2e6',
                color: isActive ? '#fff' : '#495057',
                borderRadius: 10, fontSize: '0.72rem', padding: '1px 6px', fontWeight: 700,
              }}>{t.count}</span>
            </button>
          );
        })}
      </div>

      <div className="card">
        {/* Toolbar (nur "Alle"-Tab) */}
        {activeTab === 'alle' && (
          <div className="toolbar">
            <input type="search" className="search-input" placeholder="Suche nach Name, E-Mail…"
              value={search} onChange={e => setSearch(e.target.value)} />
            <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Alle Status</option>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{WARTELISTE_STATUS[s]?.label || s}</option>
              ))}
            </select>
            <select className="filter-select" value={filterAngebot} onChange={e => setFilterAngebot(e.target.value)}>
              <option value="">Alle Platzangebote</option>
              {Object.entries(PLATZANGEBOT_ZUSTAND).map(([k, v]) => (
                <option key={k} value={k}>Platzangebot: {v.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Kategorie-Info */}
        {activeTab !== 'alle' && (
          <div style={{ padding: '0.75rem 1rem', background: '#f8f9fa', borderBottom: '1px solid var(--border)',
            fontSize: '0.82rem', color: 'var(--text-muted)', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <span>
              <strong style={{ color: 'var(--text)' }}>{rows.filter(r => !['abgelehnt','storniert'].includes(r.status)).length}</strong> aktiv wartend
            </span>
            <span>
              <strong style={{ color: '#e65100' }}>{rows.filter(r => calcAlterJahre(r.schwimmer_geburtsdatum) < MIN_ALTER_JAHRE).length}</strong> unter {MIN_ALTER_JAHRE} Jahren ⚠
            </span>
            <span>
              ↑↓ Reihenfolge ändern = manueller Eingriff wird markiert
            </span>
          </div>
        )}

        {error && <div className="alert alert-error" style={{ margin: '1rem' }}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-text">Keine Einträge gefunden</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 44 }}>Pos</th>
                  <th>Schwimmer/in</th>
                  <th>Alter</th>
                  <th>Wartezeit</th>
                  {activeTab === 'alle' && <th>Ziel</th>}
                  <th>Bisher</th>
                  <th>Status</th>
                  <th>Flags</th>
                  {activeTab !== 'alle' && <th style={{ width: 72 }}></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const alter = calcAlterJahre(r.schwimmer_geburtsdatum);
                  const zuJung = alter !== null && alter < MIN_ALTER_JAHRE;
                  const farbe = ABZEICHEN_FARBEN[r.gewuenschtes_abzeichen] || {};

                  return (
                    <tr key={r.id} onClick={() => openModal(r)}
                      style={{ cursor: 'pointer', background: zuJung ? '#fff8e1' : undefined }}>

                      <td className="td-muted" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                        {activeTab !== 'alle' ? (r.reihenfolge ?? idx + 1) : '–'}
                      </td>

                      <td>
                        <div style={{ fontWeight: 500 }}>{r.schwimmer_vorname} {r.schwimmer_nachname}</div>
                        <div className="td-muted" style={{ fontSize: '0.75rem' }}>
                          {r.kontakt_vorname} {r.kontakt_nachname}
                        </div>
                      </td>

                      <td>
                        <span>{calcAlterLabel(r.schwimmer_geburtsdatum)}</span>
                        {zuJung && (
                          <span title={`Zu jung: Mindestalter ${MIN_ALTER_JAHRE} Jahre`}
                            style={{ marginLeft: 4, background: '#fff3cd', color: '#856404',
                              borderRadius: 4, padding: '1px 5px', fontSize: '0.7rem', fontWeight: 700 }}>
                            ⚠ &lt;{MIN_ALTER_JAHRE}J
                          </span>
                        )}
                      </td>

                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: '0.85rem' }}>{calcWartezeit(r.created_at, r.wartetage)}</div>
                        <div className="td-muted" style={{ fontSize: '0.72rem' }}>
                          seit {fmtDate(r.anmeldedatum || r.created_at)}
                        </div>
                      </td>

                      {activeTab === 'alle' && (
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700,
                            background: farbe.bg || '#f0f4f8', color: farbe.color || 'var(--primary)' }}>
                            {ABZEICHEN_LABELS[r.gewuenschtes_abzeichen] || r.gewuenschtes_abzeichen}
                          </span>
                        </td>
                      )}

                      <td style={{ fontSize: '0.8rem' }}>
                        {ABZEICHEN_LABELS[r.letztes_abzeichen] || '–'}
                      </td>

                      <td>
                        <StatusBadge status={r.status} type="warteliste" />
                        {r.platzangebot_zustand && (
                          <div style={{ marginTop: 3 }}><PlatzangebotBadge zustand={r.platzangebot_zustand} /></div>
                        )}
                      </td>

                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {r.manuell_veraendert && (
                            <span title={`Manuell verändert am ${fmtDate(r.manuell_veraendert_am)} von ${r.manuell_veraendert_von || '?'}${r.manuell_veraendert_notiz ? ': ' + r.manuell_veraendert_notiz : ''}`}
                              style={{ background: '#fff3e0', color: '#e65100', borderRadius: 4,
                                padding: '1px 5px', fontSize: '0.7rem', fontWeight: 700, cursor: 'help' }}>
                              ✋ Manuell
                            </span>
                          )}
                          {r.importiert && (
                            <span title="Manuell importiert (nicht online angemeldet)"
                              style={{ background: '#e8eaf6', color: '#3949ab', borderRadius: 4,
                                padding: '1px 5px', fontSize: '0.7rem', fontWeight: 700 }}>
                              📥 Import
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Reihenfolge-Buttons (nur Kategorietabs) */}
                      {activeTab !== 'alle' && (
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 2 }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '2px 7px', fontSize: '0.75rem' }}
                              disabled={idx === 0}
                              onClick={() => initiateReorder(r.id, 'up')}
                              title="Nach oben"
                            >↑</button>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '2px 7px', fontSize: '0.75rem' }}
                              disabled={idx === rows.length - 1}
                              onClick={() => initiateReorder(r.id, 'down')}
                              title="Nach unten"
                            >↓</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginierung nur "Alle"-Tab */}
        {activeTab === 'alle' && pages > 1 && (
          <div className="pagination">
            <span className="pagination-info">{total} Einträge · Seite {page} von {pages}</span>
            <div className="pagination-btns">
              <button className="page-btn" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}>‹</button>
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" onClick={() => setPage(p => Math.min(p + 1, pages))} disabled={page === pages}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Detail-Modal ─────────────────────────────────────────────────── */}
      {selected && (
        <Modal title={`Warteliste #${selected.id} – ${selected.schwimmer_vorname} ${selected.schwimmer_nachname}`}
          onClose={closeModal} size="lg">

          {/* Abzeichen-Badge */}
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {(() => {
              const f = ABZEICHEN_FARBEN[selected.gewuenschtes_abzeichen] || {};
              return (
                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700,
                  background: f.bg || '#f0f4f8', color: f.color || 'var(--primary)' }}>
                  Ziel: {ABZEICHEN_LABELS[selected.gewuenschtes_abzeichen] || selected.gewuenschtes_abzeichen}
                </span>
              );
            })()}
            {selected.manuell_veraendert && (
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700,
                background: '#fff3e0', color: '#e65100' }}>
                ✋ Manuell verändert
                {selected.manuell_veraendert_von && ` von ${selected.manuell_veraendert_von}`}
                {selected.manuell_veraendert_am && ` am ${fmtDate(selected.manuell_veraendert_am)}`}
              </span>
            )}
            {selected.importiert && (
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700,
                background: '#e8eaf6', color: '#3949ab' }}>📥 Importierter Eintrag</span>
            )}
            {darfHinzufuegen && (
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => { const e = selected; closeModal(); setBearbeitenEintrag(e); }}
                title="Alle Angaben korrigieren, z.B. Tippfehler bei der Anmeldung">
                ✏ Daten bearbeiten
              </button>
            )}
            {(() => {
              const alter = calcAlterJahre(selected.schwimmer_geburtsdatum);
              if (alter !== null && alter < MIN_ALTER_JAHRE) return (
                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700,
                  background: '#fff3cd', color: '#856404' }}>
                  ⚠ Zu jung für Ausbildung ({alter} J., Mindestalter {MIN_ALTER_JAHRE} J.)
                </span>
              );
              return null;
            })()}
          </div>

          <div className="detail-section">Kontaktperson</div>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Name</div><div className="detail-value">{selected.kontakt_vorname} {selected.kontakt_nachname}</div></div>
            <div className="detail-item"><div className="detail-label">E-Mail</div><div className="detail-value">{selected.kontakt_email}</div></div>
            <div className="detail-item"><div className="detail-label">Telefon</div><div className="detail-value">{selected.kontakt_telefon}</div></div>
          </div>

          <div className="detail-section">Schwimmer/in</div>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Name</div><div className="detail-value">{selected.schwimmer_vorname} {selected.schwimmer_nachname}</div></div>
            <div className="detail-item">
              <div className="detail-label">Geburtsdatum</div>
              <div className="detail-value">
                {fmtDateInput(selected.schwimmer_geburtsdatum)} ({calcAlterLabel(selected.schwimmer_geburtsdatum)})
              </div>
            </div>
            <div className="detail-item"><div className="detail-label">Geschlecht</div><div className="detail-value" style={{ textTransform: 'capitalize' }}>{selected.schwimmer_geschlecht}</div></div>
            <div className="detail-item"><div className="detail-label">Letztes Abzeichen</div><div className="detail-value">{ABZEICHEN_LABELS[selected.letztes_abzeichen] || '–'}</div></div>
          </div>
          {selected.gesundheitliche_hinweise && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              <div className="detail-label">Gesundheitliche Hinweise</div>
              <div className="detail-value">{selected.gesundheitliche_hinweise}</div>
            </div>
          )}

          <div className="detail-section">Wartelisten-Details</div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-label">Anmeldedatum</div>
              <div className="detail-value">{fmtDate(selected.anmeldedatum || selected.created_at)}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Wartezeit</div>
              <div className="detail-value">{calcWartezeit(selected.created_at, selected.wartetage)}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Position (Kategorie)</div>
              <div className="detail-value">{selected.reihenfolge ?? '–'}</div>
            </div>
          </div>
          {selected.manuell_veraendert_notiz && (
            <div className="detail-item" style={{ marginBottom: '1rem' }}>
              <div className="detail-label">Notiz zur manuellen Änderung</div>
              <div className="detail-value">{selected.manuell_veraendert_notiz}</div>
            </div>
          )}

          <PlatzangebotInfo key={selected.id} wartelisteId={selected.id}
            darfBearbeiten={darfHinzufuegen} onGeaendert={() => load(page)} />

          {(['platz_angeboten', 'angenommen'].includes(selected.status) || selected.platzangebot_zustand
            || selected.aufnahmeantrag_eingang_am) && (
            <AntragWarteliste key={`antrag_${selected.id}`} eintrag={selected} darfBearbeiten={darfHinzufuegen}
              onGeaendert={(stand) => { setSelected(s => ({ ...s, ...stand })); load(page); }} />
          )}

          <div className="detail-section">Verwaltung</div>
          <div className="detail-grid">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={editStatus} onChange={e => setEditStatus(e.target.value)}>
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{WARTELISTE_STATUS[s]?.label || s}</option>
                ))}
              </select>
            </div>
          </div>
          {bietetStatusMail && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.88rem',
              background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 6, padding: '0.6rem 0.75rem', marginBottom: '1rem',
            }}>
              <input type="checkbox" checked={statusMail} onChange={e => setStatusMail(e.target.checked)}
                style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
              <span>
                Nach dem Speichern E-Mail „{STATUS_MAIL[editStatus]}" an <strong>{selected.kontakt_email}</strong> vorbereiten.
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  Die E-Mail öffnet sich vorausgefüllt und wird erst nach deiner Prüfung gesendet.
                </span>
              </span>
            </label>
          )}
          <div className="form-group">
            <label className="form-label">Interne Bemerkung</label>
            <textarea className="form-control" value={editBemerkung}
              onChange={e => setEditBemerkung(e.target.value)} rows={3}
              placeholder="Nur für Admins sichtbar…" />
          </div>

          <div className="modal-actions">
            <button className={`btn btn-danger btn-sm${deleteConfirm ? ' btn-danger-confirm' : ''}`}
              onClick={handleDelete} disabled={saving}>
              {deleteConfirm ? 'Wirklich löschen?' : 'Löschen'}
            </button>
            {deleteConfirm && <button className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirm(false)}>Abbrechen</button>}
            {selected.kontakt_email && (
              <button className="btn btn-secondary btn-sm"
                onClick={() => setEmailModal(selected)}>
                ✉ E-Mail
              </button>
            )}
            {['pirat','seepferdchen','trixi','bronze','silber','gold'].includes(selected.gewuenschtes_abzeichen) && (
              <button className="btn btn-secondary btn-sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => { closeModal(); navigate('/gruppen'); }}
                title="Zu den Ausbildungsgruppen – Kind dort aus der Warteliste aufnehmen">
                🏊 In Gruppe aufnehmen
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={closeModal}>Schließen</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </Modal>
      )}

      {emailModal && (
        <EmailModal
          an={emailModal.kontakt_email}
          anzeigeName={`${emailModal.kontakt_vorname} ${emailModal.kontakt_nachname}`}
          apiPfad={`/api/admin/warteliste/${emailModal.id}/email`}
          {...(emailModal.vorlage ? {
            titel: `E-Mail: ${STATUS_MAIL[emailModal.vorlage.status]}`,
            hinweis: `Der Status „${STATUS_MAIL[emailModal.vorlage.status]}" ist gespeichert. Bitte die E-Mail prüfen und bei Bedarf anpassen. Wird nichts gesendet, bleibt nur der Status geändert.`,
            initialBetreff: emailModal.vorlage.betreff,
            initialNachricht: emailModal.vorlage.nachricht,
            zusatzDaten: { vorlage_status: emailModal.vorlage.status },
          } : {})}
          onClose={() => setEmailModal(null)}
          onSent={() => { load(page); }}
        />
      )}

      {/* ── Reorder-Bestätigungs-Modal ─────────────────────────────────── */}
      {reorderModal && (
        <Modal title="Reihenfolge manuell ändern" onClose={() => setReorderModal(null)} size="sm">
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Diese Änderung weicht von der automatischen FIFO-Reihenfolge ab und wird dauerhaft als
            <strong> „Manuell verändert" </strong> markiert – sichtbar für alle Administratoren.
          </p>
          <div className="form-group">
            <label className="form-label">Begründung <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional, wird gespeichert)</span></label>
            <textarea className="form-control" value={reorderNotiz}
              onChange={e => setReorderNotiz(e.target.value)} rows={2}
              placeholder="z. B. Geschwisterkind, Wartezeit-Korrektur…" />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setReorderModal(null)}>Abbrechen</button>
            <button className="btn btn-primary btn-sm" onClick={confirmReorder} disabled={reordering}>
              {reordering ? 'Wird gespeichert…' : `Position ${reorderModal.direction === 'up' ? 'nach oben' : 'nach unten'} verschieben`}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Import-Modal ──────────────────────────────────────────────────── */}
      {importModal && (
        <Modal title="Warteliste importieren" onClose={closeImport} size="xl">

          {/* Import-Ergebnis */}
          {importResult && (
            <div style={{ marginBottom: '1rem', padding: '0.875rem', borderRadius: 8,
              background: importResult.fehler?.length ? '#fff3e0' : '#e8f5e9',
              border: `1px solid ${importResult.fehler?.length ? '#ffcc02' : '#a5d6a7'}` }}>
              {importResult.importiert > 0 && (
                <div style={{ color: '#2e7d32', fontWeight: 700 }}>✓ {importResult.importiert} Einträge erfolgreich importiert</div>
              )}
              {importResult.fehler?.length > 0 && (
                <>
                  <div style={{ color: '#e65100', fontWeight: 700, marginTop: 4 }}>⚠ {importResult.fehler.length} Fehler:</div>
                  <ul style={{ margin: '0.5rem 0 0 1.25rem', fontSize: '0.82rem' }}>
                    {importResult.fehler.map((f, i) => (
                      <li key={i}>Zeile {f.zeile}: {f.fehler}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* Tab-Wahl */}
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
            {[['excel', '📊 Excel-Import'], ['csv', '📋 CSV-Import'], ['manuell', '✏ Manuell eingeben']].map(([k, l]) => (
              <button key={k} onClick={() => { setImportTab(k); setImportResult(null); }}
                style={{ padding: '0.375rem 1rem', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: importTab === k ? 'var(--primary)' : '#f0f4f8',
                  color: importTab === k ? '#fff' : 'var(--text)',
                  fontWeight: importTab === k ? 700 : 400 }}>
                {l}
              </button>
            ))}
          </div>

          {/* ── Excel-Tab ──────────────────────────────────────────────── */}
          {importTab === 'excel' && (
            <>
              {/* Vorlage + Anleitung */}
              <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8,
                padding: '1rem', marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 700, color: '#1565c0', marginBottom: '0.5rem' }}>
                  📊 Excel-Import
                </div>
                <div style={{ fontSize: '0.85rem', color: '#1a237e', lineHeight: 1.6, marginBottom: '0.875rem' }}>
                  Laden Sie die Vorlage herunter, füllen Sie sie mit Ihren Wartelisten-Daten aus
                  und laden Sie die ausgefüllte Datei anschließend hier hoch.
                </div>
                <button className="btn btn-primary btn-sm" onClick={handleVorlageDownload}>
                  📥 Excel-Vorlage herunterladen
                </button>
              </div>

              {/* Upload-Bereich */}
              <div
                onClick={() => excelInputRef.current?.click()}
                style={{
                  border: `2px dashed ${excelDatei ? '#43a047' : '#90caf9'}`,
                  borderRadius: 8, padding: '1.5rem', textAlign: 'center',
                  cursor: 'pointer', marginBottom: '1rem',
                  background: excelDatei ? '#f1f8e9' : '#f8f9fa',
                  transition: 'background 0.2s, border-color 0.2s',
                }}
              >
                <input ref={excelInputRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: 'none' }} onChange={handleExcelUpload} />
                {excelDatei ? (
                  <div>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>✅</div>
                    <div style={{ fontWeight: 700, color: '#2e7d32' }}>{excelDatei.name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      Klicken zum Wechseln
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>📂</div>
                    <div style={{ fontWeight: 600, color: 'var(--text-muted)' }}>
                      Excel-Datei auswählen (.xlsx)
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      Klicken zum Durchsuchen
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button className="btn btn-secondary btn-sm" onClick={closeImport}>Abbrechen</button>
                <button className="btn btn-primary btn-sm"
                  onClick={handleExcelImport}
                  disabled={!excelDatei || importing}>
                  {importing ? 'Wird importiert…' : `${excelDatei?.name || 'Datei'} importieren`}
                </button>
              </div>
            </>
          )}

          {importTab === 'csv' && (
            <>
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '0.75rem 1rem',
                fontSize: '0.82rem', marginBottom: '1rem', lineHeight: 1.6 }}>
                <strong>CSV-Format (Semikolon-getrennt, erste Zeile = Spaltennamen):</strong><br />
                <code style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                  Anmeldedatum;Kontakt_Nachname;Kontakt_Vorname;Email;Telefon;Schwimmer_Vorname;Schwimmer_Nachname;Geburtsdatum;Geschlecht;Letztes_Abzeichen;Gewuenschtes_Abzeichen;Gesundheitliche_Hinweise
                </code><br />
                Datumsformat: <code>DD.MM.YYYY</code> oder <code>YYYY-MM-DD</code> &nbsp;|&nbsp;
                Geschlecht: <code>maennlich / weiblich / divers</code> &nbsp;|&nbsp;
                Abzeichen: <code>pirat / seepferdchen / trixi / bronze / silber / gold / schwimmen_ab_10</code>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                  📂 CSV-Datei öffnen
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
                    onChange={handleFileUpload} />
                </label>
                <button className="btn btn-secondary btn-sm" onClick={handleCsvParse} disabled={!csvText.trim()}>
                  Vorschau
                </button>
              </div>

              <textarea
                value={csvText}
                onChange={e => { setCsvText(e.target.value); setCsvPreview(null); }}
                rows={8}
                className="form-control"
                style={{ fontFamily: 'monospace', fontSize: '0.8rem', marginBottom: '1rem' }}
                placeholder="CSV hier einfügen oder Datei öffnen…"
              />

              {csvPreview && (
                <>
                  {csvPreview.fehler.length > 0 && (
                    <div style={{ color: '#c62828', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                      {csvPreview.fehler.map((f, i) => <div key={i}>⚠ {f}</div>)}
                    </div>
                  )}
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                    Vorschau: <strong>{csvPreview.eintraege.length}</strong> Einträge erkannt
                  </div>
                  <div className="table-wrap" style={{ maxHeight: 200, overflow: 'auto', marginBottom: '1rem' }}>
                    <table style={{ fontSize: '0.78rem' }}>
                      <thead>
                        <tr>
                          <th>#</th><th>Kontakt</th><th>Schwimmer</th><th>Geb.</th>
                          <th>Abzeichen</th><th>Anmeldedatum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.eintraege.map((e, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{e.kontakt_nachname}, {e.kontakt_vorname}<br /><span style={{ color: 'var(--text-muted)' }}>{e.kontakt_email}</span></td>
                            <td>{e.schwimmer_vorname} {e.schwimmer_nachname}</td>
                            <td>{e.schwimmer_geburtsdatum}</td>
                            <td>{ABZEICHEN_LABELS[e.gewuenschtes_abzeichen] || e.gewuenschtes_abzeichen || <em style={{ color: '#c00' }}>fehlt!</em>}</td>
                            <td>{e.anmeldedatum || <em style={{ color: 'var(--text-muted)' }}>heute</em>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button className="btn btn-secondary btn-sm" onClick={closeImport}>Abbrechen</button>
                <button className="btn btn-primary btn-sm"
                  onClick={() => handleImportSubmit(csvPreview?.eintraege || [])}
                  disabled={importing || !csvPreview?.eintraege?.length}>
                  {importing ? 'Importiere…' : `${csvPreview?.eintraege?.length || 0} Einträge importieren`}
                </button>
              </div>
            </>
          )}

          {importTab === 'manuell' && (
            <>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Einträge manuell hinzufügen. Das Anmeldedatum kann rückdatiert werden.
              </div>

              {manuellEintraege.map((e, i) => (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.875rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <strong style={{ fontSize: '0.85rem' }}>Eintrag {i + 1}</strong>
                    {manuellEintraege.length > 1 && (
                      <button onClick={() => setManuellEintraege(p => p.filter((_, j) => j !== i))}
                        style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '0.85rem' }}>✕ entfernen</button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem' }}>
                    {[
                      ['anmeldedatum',         'Anmeldedatum',           'date'],
                      ['kontakt_nachname',      'Kontakt Nachname *',     'text'],
                      ['kontakt_vorname',       'Kontakt Vorname *',      'text'],
                      ['kontakt_email',         'E-Mail *',               'email'],
                      ['kontakt_telefon',       'Telefon',                'tel'],
                      ['schwimmer_vorname',     'Schwimmer Vorname *',    'text'],
                      ['schwimmer_nachname',    'Schwimmer Nachname *',   'text'],
                      ['schwimmer_geburtsdatum','Geburtsdatum *',         'date'],
                    ].map(([field, label, type]) => (
                      <div key={field} className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.75rem' }}>{label}</label>
                        <input type={type} className="form-input" style={{ padding: '0.3rem 0.5rem', fontSize: '0.82rem' }}
                          value={e[field]} onChange={ev => updateManuellEintrag(i, field, ev.target.value)} />
                      </div>
                    ))}
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Geschlecht</label>
                      <select className="form-select" style={{ padding: '0.3rem 0.5rem', fontSize: '0.82rem' }}
                        value={e.schwimmer_geschlecht} onChange={ev => updateManuellEintrag(i, 'schwimmer_geschlecht', ev.target.value)}>
                        <option value="">–</option>
                        <option value="maennlich">Männlich</option>
                        <option value="weiblich">Weiblich</option>
                        <option value="divers">Divers</option>
                        <option value="unbekannt">Unbekannt</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Letztes Abzeichen</label>
                      <select className="form-select" style={{ padding: '0.3rem 0.5rem', fontSize: '0.82rem' }}
                        value={e.letztes_abzeichen} onChange={ev => updateManuellEintrag(i, 'letztes_abzeichen', ev.target.value)}>
                        <option value="">–</option>
                        {['keins','seepferdchen','trixi','bronze','silber','gold'].map(a => (
                          <option key={a} value={a}>{ABZEICHEN_LABELS[a]}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Gewünschtes Abzeichen *</label>
                      <select className="form-select" style={{ padding: '0.3rem 0.5rem', fontSize: '0.82rem' }}
                        value={e.gewuenschtes_abzeichen} onChange={ev => updateManuellEintrag(i, 'gewuenschtes_abzeichen', ev.target.value)}>
                        <option value="">– Bitte wählen –</option>
                        {ABZEICHEN_ORDER.map(a => <option key={a} value={a}>{ABZEICHEN_LABELS[a]}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              <button className="btn btn-secondary btn-sm" onClick={() => setManuellEintraege(p => [...p, { ...LEER_EINTRAG }])}
                style={{ marginBottom: '1rem' }}>
                + Weiteren Eintrag hinzufügen
              </button>

              <div className="modal-actions">
                <button className="btn btn-secondary btn-sm" onClick={closeImport}>Abbrechen</button>
                <button className="btn btn-primary btn-sm" onClick={handleManuellImport}
                  disabled={importing || !manuellEintraege.some(e => e.kontakt_nachname && e.schwimmer_vorname && e.gewuenschtes_abzeichen)}>
                  {importing ? 'Importiere…' : `${manuellEintraege.filter(e => e.kontakt_nachname && e.schwimmer_vorname && e.gewuenschtes_abzeichen).length} Einträge speichern`}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── Duplikat-Bestätigungs-Modal ───────────────────────────────────── */}
      {duplikatAktion && (
        <Modal
          title={duplikatAktion.art === 'zusammenfuehren' ? 'Einträge zusammenführen' : 'Als verschiedene Kinder markieren'}
          onClose={() => setDuplikatAktion(null)}
          size="sm"
        >
          {duplikatAktion.art === 'zusammenfuehren' ? (
            <div style={{ fontSize: '0.875rem', lineHeight: 1.7 }}>
              {duplikatAktion.behalten_id !== duplikatAktion.paar.behalten_id && (
                <div style={{ padding: '0.5rem 0.75rem', background: '#e8eaf6', borderRadius: 6,
                  marginBottom: '0.75rem', fontSize: '0.82rem', color: '#3949ab', fontWeight: 600 }}>
                  ✋ Manuelle Auswahl — weicht von der Systemempfehlung ab
                </div>
              )}
              <p>Der Eintrag <strong>#{duplikatAktion.loeschen_id}</strong> wird <strong>gelöscht</strong>.</p>
              <p>Der Eintrag <strong>#{duplikatAktion.behalten_id}</strong> wird behalten und erhält:</p>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                <li>Das <strong>ältere Anmeldedatum</strong> beider Einträge</li>
                <li>Das <strong>höhere Ausbildungsziel</strong> beider Einträge</li>
              </ul>
              <p style={{ color: '#c62828', marginTop: '0.5rem' }}>Diese Aktion kann nicht rückgängig gemacht werden.</p>
            </div>
          ) : (
            <div style={{ fontSize: '0.875rem', lineHeight: 1.7 }}>
              <p>
                Die Einträge <strong>#{duplikatAktion.paar.id_1}</strong> und <strong>#{duplikatAktion.paar.id_2}</strong> werden
                als <strong>verschiedene Kinder</strong> markiert und nicht mehr als Duplikat angezeigt.
              </p>
              <p style={{ color: 'var(--text-muted)', marginTop: '0.5rem' }}>Beide Einträge bleiben unverändert auf der Warteliste.</p>
            </div>
          )}
          <div className="modal-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setDuplikatAktion(null)}>Abbrechen</button>
            <button
              className={`btn btn-sm ${duplikatAktion.art === 'zusammenfuehren' ? 'btn-danger' : 'btn-primary'}`}
              onClick={handleDuplikatAktion}
              disabled={duplikatSaving}
            >
              {duplikatSaving ? 'Wird gespeichert…'
                : duplikatAktion.art === 'zusammenfuehren' ? 'Zusammenführen & löschen'
                : 'Als verschiedene Kinder bestätigen'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
